import { lazy, Suspense, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { SecurityProvider } from "@/components/SecurityProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { SearchHistoryProvider } from "@/contexts/SearchHistoryContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
import PageLoader from "@/components/PageLoader";
import MobileBottomNav from "./components/MobileBottomNav";
import { Toaster } from "@/components/ui/toaster";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import PWAUpdatePrompt from "@/components/PWAUpdatePrompt";
import OfflineIndicator from "@/components/OfflineIndicator";
import PushNotificationPrompt from "@/components/PushNotificationPrompt";
import AddedToCartDialog from "@/components/AddedToCartDialog";
import OneSignalInit from "@/components/OneSignalInit";


// Eager load critical routes
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";

// Lazy load all other routes
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const CollectionPage = lazy(() => import("./pages/CollectionPage"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const MetaCheckout = lazy(() => import("./pages/MetaCheckout"));
const MembershipCheckout = lazy(() => import("./pages/MembershipCheckout"));
const PaymentResult = lazy(() => import("./pages/PaymentResult"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SellerOnboarding = lazy(() => import("./pages/SellerOnboarding"));
const SellerHandbook = lazy(() => import("./pages/SellerHandbook"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const StoresShowcase = lazy(() => import("./pages/StoresShowcase"));
const OrderHistory = lazy(() => import("./pages/OrderHistory"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const Favorites = lazy(() => import("./pages/Favorites"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Support = lazy(() => import("./pages/Support"));
const FeesAndCommissions = lazy(() => import("./pages/FeesAndCommissions"));
const WhyTrustUs = lazy(() => import("./pages/WhyTrustUs"));
const ConsumerProtection = lazy(() => import("./pages/ConsumerProtection"));
const MerchantProtection = lazy(() => import("./pages/MerchantProtection"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Partnerships = lazy(() => import("./pages/Partnerships"));
const SellerStorefront = lazy(() => import("./pages/SellerStorefront"));
const SellerLanding = lazy(() => import("./pages/SellerLanding"));
const RedeemTrial = lazy(() => import("./pages/RedeemTrial"));
const SellerFAQ = lazy(() => import("./pages/SellerFAQ"));
const BuyerFAQ = lazy(() => import("./pages/BuyerFAQ"));
const Messages = lazy(() => import("./pages/Messages"));
const DigitalRights = lazy(() => import("./pages/DigitalRights"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const OrganizationAccount = lazy(() => import("./pages/OrganizationAccount"));
const PublishFair = lazy(() => import("./pages/PublishFair"));
const RequestVendorsEvent = lazy(() => import("./pages/RequestVendorsEvent"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

// Gift Registries
const GiftRegistries = lazy(() => import("./pages/GiftRegistries"));
const CreateGiftRegistry = lazy(() => import("./pages/CreateGiftRegistry"));
const MyGiftRegistries = lazy(() => import("./pages/MyGiftRegistries"));
const GiftRegistryPublic = lazy(() => import("./pages/GiftRegistryPublic"));
const EditGiftRegistry = lazy(() => import("./pages/EditGiftRegistry"));

// Admin routes
const AdminLayout = lazy(() => import("./components/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminBillingReports = lazy(() => import("./pages/AdminBillingReports"));

const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminSupportTickets = lazy(() => import("./pages/AdminSupportTickets"));
const AdminSellersReport = lazy(() => import("./pages/AdminSellersReport"));
const AdminBuyersReport = lazy(() => import("./pages/AdminBuyersReport"));


const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));

const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const AdminNotifications = lazy(() => import("./pages/AdminNotifications"));
const AdminSellerEmails = lazy(() => import("./pages/AdminSellerEmails"));
const CRBrands = lazy(() => import("./pages/CRBrands"));
const CRBrandApplication = lazy(() => import("./pages/CRBrandApplication"));
const SouvenirsLanding = lazy(() => import("./pages/landing/Souvenirs"));
const RegalosLanding = lazy(() => import("./pages/landing/Regalos"));
const ArtesaniasLanding = lazy(() => import("./pages/landing/Artesanias"));
const AdminCategorySEO = lazy(() => import("./pages/AdminCategorySEO"));
const AdminProposalBuilder = lazy(() => import("./pages/AdminProposalBuilder"));
const AdminSellerEmailEditor = lazy(() => import("./pages/AdminSellerEmailEditor"));
const AdminBuyerEmailEditor = lazy(() => import("./pages/AdminBuyerEmailEditor"));
const AdminEmailCenter = lazy(() => import("./pages/AdminEmailCenter"));

const AdminCollections = lazy(() => import("./pages/AdminCollections"));
const AdminBrandVerifications = lazy(() => import("./pages/AdminBrandVerifications"));
const AdminPrintfulOrders = lazy(() => import("./pages/AdminPrintfulOrders"));
const AdminPickupAddresses = lazy(() => import("./pages/AdminPickupAddresses"));

// Seller routes
const SellerLayout = lazy(() => import("./components/SellerLayout"));
const SellerDashboard = lazy(() => import("./pages/seller/SellerDashboard"));
const SellerProducts = lazy(() => import("./pages/seller/SellerProducts"));
const SellerProductForm = lazy(() => import("./pages/seller/SellerProductForm"));
const SellerProductImport = lazy(() => import("./pages/seller/SellerProductImport"));
const SellerOrders = lazy(() => import("./pages/seller/SellerOrders"));

const SellerSettings = lazy(() => import("./pages/seller/SellerSettings"));
const SellerMessages = lazy(() => import("./pages/seller/SellerMessages"));
const SellerVisibility = lazy(() => import("./pages/seller/SellerVisibility"));
const VisibilityPaymentResult = lazy(() => import("./pages/seller/VisibilityPaymentResult"));
const SellerReferrals = lazy(() => import("./pages/seller/SellerReferrals"));
const SellerPromotion = lazy(() => import("./pages/seller/SellerPromotion"));
const SellerReviews = lazy(() => import("./pages/seller/SellerReviews"));

const SellerSubscription = lazy(() => import("./pages/seller/SellerSubscription"));
const SubscriptionPaymentResult = lazy(() => import("./pages/seller/SubscriptionPaymentResult"));
const RecurringSubscriptionResult = lazy(() => import("./pages/seller/RecurringSubscriptionResult"));

const AdminReferrals = lazy(() => import("./pages/AdminReferrals"));
const AdminPromotionImages = lazy(() => import("./pages/AdminPromotionImages"));
const AdminAdSpots = lazy(() => import("./pages/AdminAdSpots"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminVisibilitySales = lazy(() => import("./pages/AdminVisibilitySales"));
const AdminUserManagement = lazy(() => import("./pages/AdminUserManagement"));
const AdminSubscriptions = lazy(() => import("./pages/AdminSubscriptions"));
const AdminMembershipSinpe = lazy(() => import("./pages/AdminMembershipSinpe"));
const AdminSinpePayments = lazy(() => import("./pages/AdminSinpePayments"));
const AdminCardVerification = lazy(() => import("./pages/AdminCardVerification"));
const AdminProductModeration = lazy(() => import("./pages/AdminProductModeration"));


const queryClient = new QueryClient();

const MobileNavWrapper = () => {
  const location = useLocation();
  const hideOnPaths = ['/admin', '/shop-manager'];
  const shouldHide = hideOnPaths.some(path => location.pathname.startsWith(path));
  if (shouldHide) return null;
  return <MobileBottomNav />;
};

const PaymentReturnRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hasPaymentParams =
      location.search.includes('code=') &&
      (location.search.includes('order=') || location.search.includes('orderNumber='));

    if (location.pathname === '/' && hasPaymentParams) {
      navigate(`/pago/resultado${location.search}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <LocaleProvider>
              <SecurityProvider>
                <CartProvider>
                  <FavoritesProvider>
                    <SearchHistoryProvider>
                      <PaymentReturnRedirect />
                    <div className="pb-16 md:pb-0">
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          {/* Eager loaded routes */}
                          <Route path="/" element={<Index />} />
                          <Route path="/auth" element={<Auth />} />
                          <Route path="/registro" element={<Auth mode="signup" />} />
                          <Route path="/registro-vendedor" element={<Auth mode="seller-signup" />} />
                          <Route path="/iniciar-sesion" element={<Auth mode="login" />} />
                          <Route path="/auth/callback" element={<AuthCallback />} />
                          
                          {/* Lazy loaded routes */}
                          <Route path="/productos" element={<Products />} />
                          <Route path="/productos/:id" element={<ProductDetail />} />
                          <Route path="/categoria/:slug" element={<CategoryPage />} />
                          <Route path="/coleccion/:slug" element={<CollectionPage />} />
                          <Route path="/carrito" element={<Cart />} />
                          <Route path="/checkout" element={<Checkout />} />
                          <Route path="/meta-checkout" element={<MetaCheckout />} />
                          <Route path="/membership-checkout" element={<Suspense fallback={<PageLoader />}><MembershipCheckout /></Suspense>} />
                          <Route path="/pago/resultado" element={<PaymentResult />} />
                          <Route path="/reset-password" element={<ResetPassword />} />
                          <Route path="/seller-onboarding" element={<SellerOnboarding />} />
                          <Route path="/seller-handbook" element={<SellerHandbook />} />
                          <Route path="/blog" element={<Blog />} />
                          <Route path="/blog/:slug" element={<BlogPost />} />
                          <Route path="/tiendas" element={<StoresShowcase />} />
                          
                          <Route path="/mis-pedidos" element={<OrderHistory />} />
                          <Route path="/configuracion" element={<AccountSettings />} />
                          <Route path="/favoritos" element={<Favorites />} />
                          <Route path="/soporte" element={<Navigate to="/centro-ayuda" replace />} />
                          <Route path="/centro-ayuda" element={<Support />} />
                          <Route path="/tarifas-y-comisiones" element={<FeesAndCommissions />} />
                          <Route path="/por-que-confiar" element={<WhyTrustUs />} />
                          <Route path="/proteccion-consumidor" element={<ConsumerProtection />} />
                          <Route path="/proteccion-comercio" element={<MerchantProtection />} />
                          <Route path="/quienes-somos" element={<AboutUs />} />
                          <Route path="/alianzas" element={<Partnerships />} />
                          <Route path="/partner" element={<Navigate to="/alianzas" replace />} />
                          <Route path="/vender" element={<SellerLanding />} />
                          <Route path="/activar-prueba" element={<RedeemTrial />} />
                          <Route path="/faq-vendedores" element={<SellerFAQ />} />
                          <Route path="/faq-compradores" element={<BuyerFAQ />} />
                          <Route path="/marcas-costarricenses" element={<CRBrands />} />
                          <Route path="/marcas-costarricenses/solicitud" element={<CRBrandApplication />} />
                          <Route path="/souvenirs-costa-rica" element={<SouvenirsLanding />} />
                          <Route path="/regalos-costa-rica" element={<RegalosLanding />} />
                          <Route path="/artesanias-costa-rica" element={<ArtesaniasLanding />} />
                          <Route path="/tienda/:sellerId" element={<SellerStorefront />} />
                          <Route path="/mensajes" element={<Messages />} />
                          <Route path="/derechos-digitales" element={<DigitalRights />} />
                          <Route path="/terminos-y-condiciones" element={<TermsAndConditions />} />
                          <Route path="/politica-de-privacidad" element={<PrivacyPolicy />} />
                          <Route path="/cuenta-organizaciones" element={<OrganizationAccount />} />
                         <Route path="/publicar-feria" element={<PublishFair />} />
                         <Route path="/emprendimientos-para-eventos" element={<RequestVendorsEvent />} />
                         <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

                          
                          {/* Gift Registries */}
                          <Route path="/listas-de-regalos" element={<GiftRegistries />} />
                          <Route path="/listas-de-regalos/crear" element={<CreateGiftRegistry />} />
                          <Route path="/listas-de-regalos/mis-listas" element={<MyGiftRegistries />} />
                          <Route path="/listas-de-regalos/editar/:slug" element={<EditGiftRegistry />} />
                          <Route path="/listas-de-regalos/:slug" element={<GiftRegistryPublic />} />
                          
                          {/* Seller Shop Manager */}
                          <Route path="/shop-manager" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerDashboard /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/productos" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerProducts /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/productos/nuevo" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerProductForm /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/productos/importar" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerProductImport /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/productos/:id" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerProductForm /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/pedidos" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerOrders /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/estadisticas" element={<Navigate to="/shop-manager" replace />} />
                          <Route path="/shop-manager/mensajes" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerMessages /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/configuracion" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerSettings /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/visibilidad" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerVisibility /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/visibilidad/resultado" element={<Suspense fallback={<PageLoader />}><SellerLayout><VisibilityPaymentResult /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/referidos" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerReferrals /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/promocionar" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerPromotion /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/resenas" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerReviews /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/pagos-sinpe" element={<Navigate to="/shop-manager/pedidos" replace />} />
                          <Route path="/shop-manager/membresia" element={<Suspense fallback={<PageLoader />}><SellerLayout><SellerSubscription /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/membresia/resultado" element={<Suspense fallback={<PageLoader />}><SellerLayout><SubscriptionPaymentResult /></SellerLayout></Suspense>} />
                          <Route path="/shop-manager/membresia/suscripcion-resultado" element={<Suspense fallback={<PageLoader />}><SellerLayout><RecurringSubscriptionResult /></SellerLayout></Suspense>} />
                          
{/* Admin routes */}
                          <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<AdminDashboard />} />
                            <Route path="colecciones" element={<AdminCollections />} />
                            <Route path="categorias-seo" element={<AdminCategorySEO />} />
                            <Route path="marcas" element={<AdminBrandVerifications />} />
                            <Route path="sellers-report" element={<AdminSellersReport />} />
                            <Route path="buyers-report" element={<AdminBuyersReport />} />
                            <Route path="printful-orders" element={<AdminPrintfulOrders />} />
                            <Route path="pickup-addresses" element={<AdminPickupAddresses />} />
                            <Route path="billing-reports" element={<Navigate to="/admin/orders?tab=facturacion" replace />} />
                            
                            <Route path="settings" element={<AdminSettings />} />
                            <Route path="support-tickets" element={<AdminSupportTickets />} />
                            
                            
                            
                            <Route path="analytics" element={<Navigate to="/admin/orders?tab=analiticas" replace />} />
                            <Route path="marketing" element={<AdminMarketing />} />
                            <Route path="notifications" element={<AdminNotifications />} />
                            <Route path="correos" element={<AdminEmailCenter />} />
                            <Route path="email-vendedores" element={<AdminSellerEmails />} />
                            <Route path="propuestas" element={<AdminProposalBuilder />} />
                            <Route path="editor-correos-vendedores" element={<AdminSellerEmailEditor />} />
                            <Route path="editor-correos-compradores" element={<AdminBuyerEmailEditor />} />


                            <Route path="referidos" element={<AdminReferrals />} />
                            <Route path="promotion-images" element={<AdminPromotionImages />} />
                            <Route path="ad-spots" element={<AdminAdSpots />} />
                            <Route path="orders" element={<AdminOrders />} />
                            <Route path="visibility-sales" element={<AdminVisibilitySales />} />
                            <Route path="admins" element={<AdminUserManagement />} />
                            <Route path="membresias" element={<AdminSubscriptions />} />
                            <Route path="membresias-sinpe" element={<AdminMembershipSinpe />} />
                            <Route path="pagos-sinpe" element={<AdminSinpePayments />} />
                            <Route path="verificacion-tarjetas" element={<AdminCardVerification />} />
                            <Route path="moderacion-productos" element={<AdminProductModeration />} />
                          </Route>

                          
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </div>
                      <MobileNavWrapper />
                      <Toaster />
                      <AddedToCartDialog />
                      <PWAInstallPrompt />
                      <PWAUpdatePrompt />
                      <OfflineIndicator />
                      <PushNotificationPrompt />
                      <OneSignalInit />

                    </SearchHistoryProvider>
                  </FavoritesProvider>
                </CartProvider>
              </SecurityProvider>
            </LocaleProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
